/* eslint-disable no-restricted-globals, no-alert */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { StoredQuestion, StoredCategory, saveQuestionsToStorage, getQuestionsFromStorage, saveCategoriesToStorage, getCategoriesFromStorage } from '../utils/storage';
import { insertQuestion, fetchQuestionById, updateQuestion, fetchExamSets, getExamIdFromType, ExamSetSummary } from '../utils/questionApi';

type ExamType = { value: string; label: string; emoji: string };
type ExamCategory = { value: number; label: string; exam: string };

// Exam types for question bank
const EXAM_TYPES: ExamType[] = [
  { value: 'saa', label: 'AWS Solutions Architect (SAA-C03)', emoji: '🏗️' },
  { value: 'sap', label: 'AWS Solutions Architect Professional (SAP-C02)', emoji: '🎓' },
  { value: 'ccp', label: 'AWS Cloud Practitioner (CCP)', emoji: '☁️' },
  { value: 'dva', label: 'AWS Developer Associate (DVA)', emoji: '💻' },
  { value: 'sysdesign', label: 'System Design', emoji: '⚙️' },
  { value: 'dsa', label: 'Data Structures & Algorithms', emoji: '🧩' },
  { value: 'python', label: 'Python', emoji: '🐍' },
  { value: 'dotnet', label: '.NET / Java', emoji: '🔷' },
  { value: 'aiml', label: 'AI & Machine Learning', emoji: '🤖' },
];

// Exam categories for question bank
const EXAM_CATEGORIES: ExamCategory[] = [
  // SAA-C03 Categories
  { value: 1, label: '1. Hybrid DNS & Failover', exam: 'saa' },
  { value: 2, label: '2. Compute & Auto Scaling', exam: 'saa' },
  { value: 3, label: '3. Security & IAM', exam: 'saa' },
  { value: 4, label: '4. Storage & Data Migration', exam: 'saa' },
  { value: 5, label: '5. Networking & VPC', exam: 'saa' },
  { value: 6, label: '6. Databases & Caching', exam: 'saa' },
  { value: 7, label: '7. Serverless & Lambda', exam: 'saa' },
  { value: 8, label: '8. High Availability & DR', exam: 'saa' },
  { value: 9, label: '9. Cost Optimization', exam: 'saa' },
  { value: 10, label: '10. Monitoring & Governance', exam: 'saa' },
  { value: 11, label: '11. Multi-Account & Org', exam: 'saa' },
  { value: 12, label: '12. Analytics & Big Data', exam: 'saa' },
  { value: 13, label: '13. Containers & ECS/EKS', exam: 'saa' },
  { value: 14, label: '14. Migration & Hybrid Cloud', exam: 'saa' },
  { value: 15, label: '15. Advanced Networking', exam: 'saa' },
  { value: 16, label: '16. Exam Simulation Set', exam: 'saa' },
  // SAP-C02 Categories
  { value: 101, label: '1. Design for Organizational Complexity', exam: 'sap' },
  { value: 102, label: '2. Design for New Solutions', exam: 'sap' },
  { value: 103, label: '3. Migration Planning', exam: 'sap' },
  { value: 104, label: '4. Cost Optimization', exam: 'sap' },
  { value: 105, label: '5. Security & Compliance', exam: 'sap' },
  { value: 106, label: '6. Network & Hybrid Architecture', exam: 'sap' },
  { value: 107, label: '7. Multi-Account Strategy', exam: 'sap' },
  { value: 108, label: '8. Business Continuity', exam: 'sap' },
  { value: 109, label: '9. Deployment & Operations', exam: 'sap' },
  { value: 110, label: '10. Data Protection & Privacy', exam: 'sap' },
  // CCP Categories
  { value: 201, label: '1. Cloud Concepts', exam: 'ccp' },
  { value: 202, label: '2. AWS Security', exam: 'ccp' },
  { value: 203, label: '3. Technology', exam: 'ccp' },
  { value: 204, label: '4. Billing & Pricing', exam: 'ccp' },
  // DVA Categories
  { value: 301, label: '1. Development with AWS Services', exam: 'dva' },
  { value: 302, label: '2. Security', exam: 'dva' },
  { value: 303, label: '3. Deployment', exam: 'dva' },
  { value: 304, label: '4. Troubleshooting & Optimization', exam: 'dva' },
  // System Design
  { value: 401, label: '1. Scalability', exam: 'sysdesign' },
  { value: 402, label: '2. High Availability', exam: 'sysdesign' },
  { value: 403, label: '3. Storage & Databases', exam: 'sysdesign' },
  { value: 404, label: '4. Caching & CDN', exam: 'sysdesign' },
  { value: 405, label: '5. Microservices', exam: 'sysdesign' },
  // DSA
  { value: 501, label: '1. Arrays & Strings', exam: 'dsa' },
  { value: 502, label: '2. Linked Lists', exam: 'dsa' },
  { value: 503, label: '3. Trees & Graphs', exam: 'dsa' },
  { value: 504, label: '4. Dynamic Programming', exam: 'dsa' },
  { value: 505, label: '5. Sorting & Searching', exam: 'dsa' },
  // Python
  { value: 601, label: '1. Python Basics', exam: 'python' },
  { value: 602, label: '2. Functions & Modules', exam: 'python' },
  { value: 603, label: '3. OOP', exam: 'python' },
  { value: 604, label: '4. Data Structures', exam: 'python' },
  { value: 605, label: '5. File I/O & Exceptions', exam: 'python' },
  // .NET / Java
  { value: 701, label: '1. C# Basics', exam: 'dotnet' },
  { value: 702, label: '2. .NET Framework', exam: 'dotnet' },
  { value: 703, label: '3. ASP.NET Core', exam: 'dotnet' },
  { value: 704, label: '4. Entity Framework', exam: 'dotnet' },
  // AI & ML
  { value: 801, label: '1. ML Basics', exam: 'aiml' },
  { value: 802, label: '2. Neural Networks', exam: 'aiml' },
  { value: 803, label: '3. Deep Learning', exam: 'aiml' },
  { value: 804, label: '4. NLP', exam: 'aiml' },
];

const AddQuestionPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Check admin auth
  useEffect(() => {
    if (sessionStorage.getItem('adminAuth') !== '1') {
      navigate('/admin-login');
    }
  }, [navigate]);

  // Get edit question ID from URL if present
  const editId = searchParams.get('edit');
  
  // State for form
  const [formExam, setFormExam] = useState('');
  const [formCategory, setFormCategory] = useState('');
  const [formQuestion, setFormQuestion] = useState('');
  const [formOptions, setFormOptions] = useState<string[]>(['', '', '', '', '']);
  const [formCorrectAnswer, setFormCorrectAnswer] = useState<number | number[]>(0);
  const [formTotalOptions, setFormTotalOptions] = useState(4);
  const [formExplanation, setFormExplanation] = useState('');
  const [formSet, setFormSet] = useState('');
  const [examSets, setExamSets] = useState<ExamSetSummary[]>([]);
  
  const [questions, setQuestions] = useState<StoredQuestion[]>([]);
  const [categories, setCategories] = useState<StoredCategory[]>([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Load questions on mount
  useEffect(() => {
    setQuestions(getQuestionsFromStorage());
    setCategories(getCategoriesFromStorage());
  }, []);

  // Existing sets of the selected exam (from question_master.set)
  const loadSets = React.useCallback(async (examType: string) => {
    if (!examType) { setExamSets([]); return; }
    const res = await fetchExamSets(getExamIdFromType(examType));
    setExamSets(res.success && res.sets ? res.sets : []);
  }, []);

  useEffect(() => { loadSets(formExam); }, [formExam, loadSets]);

  // Load existing question for editing
  useEffect(() => {
    if (editId) {
      const isFromDb = searchParams.get('from') === 'db';
      
      if (isFromDb) {
        // Fetch from database
        const loadFromDb = async () => {
          const questionId = parseInt(editId, 10);
          if (!isNaN(questionId)) {
            const result = await fetchQuestionById(questionId);
            if (result.success && result.data) {
              const q = result.data;
              // Convert exam_id to exam type string
              const examTypeMap: Record<number, string> = {
                1: 'saa', 2: 'sap', 3: 'ccp', 4: 'dva',
                5: 'sysdesign', 6: 'dsa', 7: 'python', 8: 'dotnet', 9: 'aiml'
              };
              setFormExam(examTypeMap[q.exam_id] || 'saa');
              setFormCategory(String(q.category_id));
              setFormQuestion(q.question);
              setFormOptions([q.option1 || '', q.option2 || '', q.option3 || '', q.option4 || '', q.option5 || '', q.option6 || '']);
              
              // Parse correct_ans (comma-separated string like "1" or "1,3")
              const correctAnsParts = (q.correct_ans || '').split(',');
              if (correctAnsParts.length > 1) {
                setFormCorrectAnswer(correctAnsParts.map(p => Math.max(0, parseInt(p, 10) - 1)));
              } else {
                const parsed = parseInt(q.correct_ans || '1', 10);
                setFormCorrectAnswer(isNaN(parsed) ? 0 : Math.max(0, parsed - 1));
              }
              
              setFormTotalOptions(q.total_option || 4);
              setFormExplanation(q.explaination || '');
              setFormSet(q.set !== null && q.set !== undefined ? String(q.set) : '');
            }
          }
        };
        loadFromDb();
      } else {
        // Load from local storage
        const allQuestions = getQuestionsFromStorage();
        const questionToEdit = allQuestions.find(q => q.id === editId);
        if (questionToEdit) {
          setFormExam(questionToEdit.examId || '');
          setFormCategory(questionToEdit.categoryId);
          setFormQuestion(questionToEdit.question);
          setFormOptions([...questionToEdit.options]);
          setFormCorrectAnswer(
            Array.isArray(questionToEdit.correctAnswer)
              ? questionToEdit.correctAnswer
              : questionToEdit.correctAnswer
          );
          setFormTotalOptions(questionToEdit.options?.length || 4);
          setFormExplanation(questionToEdit.explanation);
        }
      }
    }
  }, [editId, searchParams]);

  const handleSubmit = async () => {
    if (!formExam || !formCategory || !formQuestion.trim() || formOptions.slice(0, formTotalOptions).some(o => !o.trim()) || formExplanation.trim() === '') {
      alert('Please fill in all required fields');
      return;
    }

    setSaving(true);

    // First, save to local storage
    const newQuestion: StoredQuestion = {
      id: editId || 'q_' + Date.now(),
      examId: formExam,
      categoryId: formCategory,
      question: formQuestion.trim(),
      options: formOptions.slice(0, formTotalOptions),
      correctAnswer: formCorrectAnswer,
      explanation: formExplanation.trim()
    };

    let updated: StoredQuestion[];
    if (editId) {
      updated = questions.map(q => q.id === editId ? newQuestion : q);
    } else {
      updated = [...questions, newQuestion];
    }

    // Remove any duplicate questions by ID before saving
    const uniqueQuestions = updated.filter((q, index, self) => 
      index === self.findIndex(t => t.id === q.id)
    );
    
    setQuestions(uniqueQuestions);
    saveQuestionsToStorage(uniqueQuestions);

    // Auto-create category if doesn't exist
    const cat = categories.find(c => c.id === formCategory);
    if (!cat) {
      const newCat: StoredCategory = { id: formCategory, name: formCategory, emoji: '📝' };
      const updatedCats = [...categories, newCat];
      setCategories(updatedCats);
      saveCategoriesToStorage(updatedCats);
    }

    // Also save to Supabase
    try {
      const isFromDb = searchParams.get('from') === 'db';
      
      if (isFromDb && editId) {
        // Update existing question in database
        const questionId = parseInt(editId, 10);
        console.log('Editing question from DB, ID:', questionId, 'editId:', editId);
        if (!isNaN(questionId)) {
          const supabaseResult = await updateQuestion(questionId, {
            examId: formExam,
            categoryId: formCategory,
            question: formQuestion.trim(),
            options: formOptions.slice(0, formTotalOptions),
            correctAnswer: formCorrectAnswer,
            explanation: formExplanation.trim(),
            set: formSet === '' ? null : parseInt(formSet, 10)
          });
          
          console.log('Update result:', supabaseResult);
          if (!supabaseResult.success) {
            console.error('Failed to update in Supabase:', supabaseResult.error);
            alert('Failed to update in database: ' + supabaseResult.error);
          } else {
            console.log('Question updated successfully in database');
          }
        }
      } else {
        // Insert new question to database
        const supabaseResult = await insertQuestion({
          examId: formExam,
          categoryId: formCategory,
          question: formQuestion.trim(),
          options: formOptions.slice(0, formTotalOptions),
          correctAnswer: formCorrectAnswer,
          explaination: formExplanation.trim(),
          set: formSet === '' ? null : parseInt(formSet, 10)
        });
        
        if (!supabaseResult.success) {
          console.error('Failed to save to Supabase:', supabaseResult.error);
          alert('Saved locally, but failed to save to the database: ' + supabaseResult.error);
        }
      }
    } catch (supabaseError) {
      console.error('Supabase save error:', supabaseError);
    }

    setSaving(false);
    setSaved(true);

    // Check if question was edited from database
    const isFromDb = searchParams.get('from') === 'db';
    
    // Redirect back to the appropriate page
    setTimeout(() => {
      if (isFromDb) {
        navigate('/admin/manage-questions');
      } else {
        navigate('/admin?panel=questions');
      }
    }, 1000);
  };

  const handleCancel = () => {
    const isFromDb = searchParams.get('from') === 'db';
    if (isFromDb) {
      navigate('/admin/manage-questions');
    } else {
      navigate('/admin?panel=questions');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--gray-50)', padding: '20px' }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div>
            <button
              onClick={handleCancel}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.9rem',
                color: 'var(--gray-600)',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                marginBottom: '8px'
              }}
            >
              ← Back to Questions
            </button>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--gray-800)' }}>
              {editId ? '✏️ Edit Question' : '➕ Add New Question'}
            </h1>
          </div>
        </div>

        {/* Success Message */}
        {saved && (
          <div style={{
            background: 'var(--green)',
            color: 'white',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '16px',
            fontWeight: 500
          }}>
            ✓ Question saved successfully! Redirecting...
          </div>
        )}

        {/* Form */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Exam Type */}
            <div>
              <label style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--gray-700)', display: 'block', marginBottom: '6px' }}>
                Exam Type <span style={{ color: 'var(--red)' }}>*</span>
              </label>
              <select
                value={formExam}
                onChange={e => { setFormExam(e.target.value); setFormCategory(''); setFormSet(''); }}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--gray-300)', borderRadius: '6px', fontSize: '0.95rem' }}
              >
                <option value="">Select Exam</option>
                {EXAM_TYPES.map(exam => (
                  <option key={exam.value} value={exam.value}>{exam.emoji} {exam.label}</option>
                ))}
              </select>
            </div>

            {/* Category */}
            <div>
              <label style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--gray-700)', display: 'block', marginBottom: '6px' }}>
                Category <span style={{ color: 'var(--red)' }}>*</span>
              </label>
              <select
                value={formCategory}
                onChange={e => setFormCategory(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--gray-300)', borderRadius: '6px', fontSize: '0.95rem' }}
                disabled={!formExam}
              >
                <option value="">Select Category</option>
                {formExam ? (
                  <>
                    <optgroup label="AWS Exam Categories">
                      {EXAM_CATEGORIES.filter(cat => cat.exam === formExam).map(cat => (
                        <option key={cat.value} value={String(cat.value)}>{cat.label}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Custom Categories">
                      {categories.filter(cat => cat.id.startsWith(formExam + '_')).map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.emoji} {cat.name}</option>
                      ))}
                    </optgroup>
                  </>
                ) : (
                  <optgroup label="Custom Categories">
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.emoji} {cat.name}</option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>

            {/* Set */}
            <div>
              <label style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--gray-700)', display: 'block', marginBottom: '6px' }}>
                Set
              </label>
              {(() => {
                const nextSet = examSets.length ? Math.max(...examSets.map(x => x.set)) + 1 : 1;
                const current = formSet !== '' ? parseInt(formSet, 10) : null;
                const knownCurrent = current !== null && examSets.some(x => x.set === current);
                return (
                  <select
                    value={formSet}
                    onChange={e => setFormSet(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--gray-300)', borderRadius: '6px', fontSize: '0.95rem' }}
                    disabled={!formExam}
                  >
                    <option value="">— No set —</option>
                    {examSets.map(x => (
                      <option key={x.set} value={String(x.set)}>Set {x.set} ({x.count} questions)</option>
                    ))}
                    {current !== null && !knownCurrent && current !== nextSet && <option value={String(current)}>Set {current} (new)</option>}
                    <option value={String(nextSet)}>➕ New set: Set {nextSet}</option>
                  </select>
                );
              })()}
              <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)', marginTop: '4px' }}>
                Questions are grouped into sets for the exam pages. A question without a set will not appear in any set.
              </div>
            </div>

            {/* Question */}
            <div>
              <label style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--gray-700)', display: 'block', marginBottom: '6px' }}>
                Question (HTML supported) <span style={{ color: 'var(--red)' }}>*</span>
              </label>
              <textarea
                value={formQuestion}
                onChange={e => setFormQuestion(e.target.value)}
                rows={6}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--gray-300)', borderRadius: '6px', fontSize: '0.95rem', resize: 'vertical' }}
                placeholder="Enter your question here... (HTML tags supported)"
              />
            </div>

            {/* Options */}
            <div>
              <label style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--gray-700)', display: 'block', marginBottom: '6px' }}>
                Options <span style={{ color: 'var(--red)' }}>*</span>
              </label>
              <div style={{ marginBottom: '12px' }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--gray-600)' }}>Total Options: </span>
                <select
                  value={formTotalOptions}
                  onChange={e => {
                    const newTotal = parseInt(e.target.value);
                    setFormTotalOptions(newTotal);
                    const updated = [...formOptions];
                    while (updated.length < newTotal) updated.push('');
                    setFormOptions(updated);
                    const currentAns = Array.isArray(formCorrectAnswer) ? formCorrectAnswer[0] : formCorrectAnswer;
                    if (currentAns >= newTotal) setFormCorrectAnswer(0);
                  }}
                  style={{ padding: '4px 8px', border: '1px solid var(--gray-300)', borderRadius: '4px', fontSize: '0.85rem', marginLeft: '8px' }}
                >
                  <option value={2}>2</option>
                  <option value={3}>3</option>
                  <option value={4}>4</option>
                  <option value={5}>5</option>
                  <option value={6}>6</option>
                </select>
                <label style={{ marginLeft: '20px', fontWeight: 400, color: 'var(--gray-700)', fontSize: '0.85rem' }}>
                  <input
                    type="checkbox"
                    checked={Array.isArray(formCorrectAnswer)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormCorrectAnswer([0]);
                      } else {
                        setFormCorrectAnswer(0);
                      }
                    }}
                    style={{ marginRight: '6px' }}
                  />
                  Multiple correct answers
                </label>
              </div>
              {formOptions.slice(0, formTotalOptions).map((opt, i) => (
                <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'flex-start' }}>
                  <input
                    type="checkbox"
                    checked={Array.isArray(formCorrectAnswer) ? formCorrectAnswer.includes(i) : formCorrectAnswer === i}
                    onChange={(e) => {
                      if (e.target.checked) {
                        if (Array.isArray(formCorrectAnswer)) {
                          setFormCorrectAnswer([...formCorrectAnswer, i]);
                        } else {
                          setFormCorrectAnswer([formCorrectAnswer as number, i]);
                        }
                      } else {
                        if (Array.isArray(formCorrectAnswer)) {
                          setFormCorrectAnswer(formCorrectAnswer.filter(ans => ans !== i));
                        }
                      }
                    }}
                    style={{ width: '18px', height: '18px', accentColor: 'var(--blue)', marginTop: '8px' }}
                    title={`Mark Option ${String.fromCharCode(65 + i)} as correct`}
                  />
                  <span style={{ minWidth: '24px', fontSize: '0.85rem', color: 'var(--gray-500)', marginTop: '8px' }}>{String.fromCharCode(65 + i)}.</span>
                  <textarea
                    value={opt}
                    onChange={e => {
                      const updated = [...formOptions];
                      updated[i] = e.target.value;
                      setFormOptions(updated);
                    }}
                    placeholder={`Option ${String.fromCharCode(65 + i)}`}
                    rows={2}
                    style={{ flex: 1, padding: '8px 10px', border: '1px solid var(--gray-300)', borderRadius: '6px', fontSize: '0.9rem', resize: 'vertical' }}
                  />
                </div>
              ))}
            </div>

            {/* Explanation */}
            <div>
              <label style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--gray-700)', display: 'block', marginBottom: '6px' }}>
                Explanation (HTML supported) <span style={{ color: 'var(--red)' }}>*</span>
              </label>
              <textarea
                value={formExplanation}
                onChange={e => setFormExplanation(e.target.value)}
                rows={6}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--gray-300)', borderRadius: '6px', fontSize: '0.95rem', resize: 'vertical' }}
                placeholder="Explain why the correct answer is correct... (HTML supported)"
              />
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px', paddingTop: '16px', borderTop: '1px solid var(--gray-200)' }}>
              <button
                className="btn btn-outline-gray"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="btn btn-blue"
                onClick={handleSubmit}
                disabled={saving}
                style={{ minWidth: '120px' }}
              >
                {saving ? 'Saving...' : saved ? 'Saved!' : editId ? 'Update' : 'Add'} Question
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddQuestionPage;